import { useMutation } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import {
  addBanner,
  addCategory,
  addComingSoon,
  addLiveChannel,
  addMovie,
  addSeason,
  addSeries,
  addSubCategory,
  addSubscription,
  addUser,
  addVerticalSeries,
  deleteSeason,
  updateBanner,
  updateCategory,
  updateComingSoon,
  updateLiveChannel,
  updateMovie,
  updateSeason,
  updateSeries,
  updateSubCategory,
  updateSubscription,
  updateUser,
  updateVerticalSeries,
} from "./api";

const showSuccess = (msg) => notifications.show({ message: msg, color: "green" });
const showError = (msg) => notifications.show({ message: msg, color: "red" });

export const useAddBanner = () => {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: addBanner,
    onSuccess: () => navigate("/banners"),
  });
};

export const useUpdateBanner = () => {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: updateBanner,
    onSuccess: () => navigate("/banners"),
  });
};

export const useAddCategory = () => {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: addCategory,
    onSuccess: () => navigate("/categories"),
  });
};

export const useUpdateCategory = () => {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: updateCategory,
    onSuccess: () => navigate("/categories"),
  });
};

export const useAddMovie = () => {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: addMovie,
    onSuccess: (data) => {
      const route =
        data.type === "Documentary" ? "/documentaries"
        : data.type === "Music Video" ? "/music-videos"
        : data.type === "Short Film" ? "/short-films"
        : "/movies";
      navigate(route);
    },
  });
};

export const useUpdateMovie = ({ onSuccess }) => {
  return useMutation({
    mutationFn: updateMovie,
    onSuccess: (data) => onSuccess(data),
  });
};

export const useAddSeries = () => {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: addSeries,
    onSuccess: () => navigate("/series"),
  });
};

export const useUpdateSeries = ({ onSuccess }) => {
  return useMutation({
    mutationFn: updateSeries,
    onSuccess: () => onSuccess(),
  });
};

export const useAddUser = () => {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: addUser,
    onSuccess: () => navigate("/users"),
  });
};

export const useUpdateUser = ({ onSuccess }) => {
  return useMutation({
    mutationFn: updateUser,
    onSuccess: () => onSuccess(),
  });
};

export const useAddSubCategory = () => {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: addSubCategory,
    onSuccess: (data) => navigate(`/subcategories/${data?.category}/Sub-Category`),
  });
};

export const useUpdateSubCategory = () => {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: updateSubCategory,
    onSuccess: (data) => navigate(`/subcategories/${data?.category}/Sub-Category`),
  });
};

export const useAddSubcription = () => {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: addSubscription,
    onSuccess: () => navigate(`/subscription`),
  });
};

export const useUpdateSubcription = () => {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: updateSubscription,
    onSuccess: () => navigate(`/subscription`),
  });
};

export const useAddComingSoon = () => {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: addComingSoon,
    onSuccess: () => navigate(`/coming-soon`),
  });
};

export const useUpdateComingSoon = () => {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: updateComingSoon,
    onSuccess: () => navigate(`/coming-soon`),
  });
};

export const useAddSeason = () => {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: addSeason,
    onSuccess: ([success, response], variables) => {
      if (success) { showSuccess("Season added successfully"); navigate(`/seasons/${variables.series}`); }
      else { showError(response.message); }
    },
  });
};

export const useUpdateSeason = () => {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: ({ id, data }) => updateSeason(id, data),
    onSuccess: ([success, response]) => {
      if (success) { showSuccess("Season updated successfully"); navigate(`/seasons/${response.series}`); }
      else { showError(response.message); }
    },
  });
};

export const useDeleteSeason = () => {
  return useMutation({
    mutationFn: deleteSeason,
    onSuccess: ([success, response]) => {
      if (success) showSuccess("Season deleted successfully");
      else showError(response.message);
    },
  });
};

export const useAddVerticalSeries = () => {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: addVerticalSeries,
    onSuccess: () => { showSuccess("Short Drama added successfully"); navigate("/vertical-series"); },
    onError: (error) => {
      console.error("Add Short Drama error:", error);
      showError(error?.response?.data?.message || "Failed to add Short Drama");
    },
  });
};

export const useUpdateVerticalSeries = ({ onSuccess }) => {
  return useMutation({
    mutationFn: updateVerticalSeries,
    onSuccess: () => { showSuccess("Short Drama updated successfully"); onSuccess(); },
    onError: (error) => {
      console.error("Update Short Drama error:", error);
      showError(error?.response?.data?.message || "Failed to update Short Drama");
    },
  });
};

export const useAddLiveChannel = () => {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: addLiveChannel,
    onSuccess: () => { showSuccess("Live channel added successfully"); navigate("/live-channels"); },
    onError: (error) => showError(error?.response?.data?.message || "Failed to add live channel"),
  });
};

export const useUpdateLiveChannel = () => {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: updateLiveChannel,
    onSuccess: () => { showSuccess("Live channel updated successfully"); navigate("/live-channels"); },
    onError: (error) => showError(error?.response?.data?.message || "Failed to update live channel"),
  });
};
